<template>
  <div class="login-form">
    <div class="mobile-logo mb-6" @click="home">
      <span class="brand-smpl">smpl</span>
      <span class="brand-sale">sale</span>
    </div>

    <div class="form-title">Login</div>
    <div class="form-subtitle">Enter your account details to continue.</div>

    <v-form class="mt-6" @submit.prevent="submit">
      <v-text-field
        v-model="code"
        outlined
        ref="locationCode"
        dense
        :error-messages="codeErrors"
        label="Code"
        prepend-inner-icon="mdi-store-outline"
      >
        <template slot="append">
          <v-btn small text color="primary" @click="$router.push({ path: '/forgot' })">
            Forgot
          </v-btn>
        </template>
      </v-text-field>

      <v-text-field
        v-model="email"
        outlined
        dense
        :error-messages="emailErrors"
        label="Email"
        prepend-inner-icon="mdi-email-outline"
      />

      <v-text-field
        v-model="password"
        outlined
        dense
        :append-icon="showPassword ? 'visibility_off' : 'visibility'"
        :type="showPassword ? 'text' : 'password'"
        :label="$t('labels.password')"
        prepend-inner-icon="mdi-lock-outline"
        v-on:keyup.enter="submit"
        :error-messages="passwordErrors"
        @input="$v.password.$touch()"
        @click:append="showPassword = !showPassword"
      />

      <div class="form-actions">
        <v-btn text color="primary" @click="$router.push({ path: '/forgot' })">
          Forgot password?
        </v-btn>

        <v-btn
          :loading="loading"
          type="submit"
          color="primary"
          rounded
          depressed
          class="login-btn"
        >
          {{ $t("buttons.login") }}
        </v-btn>
      </div>
    </v-form>

    <v-divider class="my-5" />

    <v-btn
      text
      block
      color="primary"
      @click="$router.push({ path: '/signup' })"
    >
      Don't have an account?
    </v-btn>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import authService from "../service";

const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

export default {
  name: "authentication-login",

  mixins: [validationMixin],

  data() {
    return {
      showPassword: false,
      email: null,
      loading: false,
      password: null,
      code: null,
    };
  },

  validations: {
    email: {
      email,
      required,
    },
    code: {
      required,
    },
    password: {
      required,
    },
  },

  created() {
    const localStoreCode = localStorage.getItem(
      `${process.env.VUE_APP_STORAGE_PREFIX}code`
    );

    if (localStoreCode) {
      this.code = localStoreCode;
    }
  },

  computed: {
    codeErrors() {
      const errors = [];

      if (!this.$v.code.$dirty) return errors;
      if (!this.$v.code.required) errors.push("Code is required.");

      return errors;
    },

    emailErrors() {
      const errors = [];

      if (!this.$v.email.$dirty) return errors;
      if (!this.$v.email.email) errors.push("Email is not valid");
      if (!this.$v.email.required) errors.push("Email is required.");

      return errors;
    },

    passwordErrors() {
      const errors = [];

      if (!this.$v.password.$dirty) return errors;
      if (!this.$v.password.required) errors.push("Password is required.");

      return errors;
    },
  },

  methods: {
    ...mapActions("global", ["initBusiness", "initUser"]),

    home() {
      window.open("https://smplsale.com", "_self");
    },

    checkResponse(response) {
      if (response.status === 400) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Invalid User",
          });

        return;
      }

      localStorage.setItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}code`,
        this.code
      );

      this.$router.push({ name: "home" });
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      this.loading = true;
      this.$Progress.start();

      authService
        .authenticate({
          email: this.email,
          password: this.password,
          code: this.code,
        })
        .then((response) => {
          this.$Progress.finish();
          this.checkResponse(response);
        })
        .catch((error) => {
          this.$Progress.finish();

          const message =
            error && error.data && error.data.message
              ? error.data.message
              : "Unable to login";

          this.$swal("Invalid Login", message, "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.login-form {
  width: 100%;
  max-width: 420px;
}

.mobile-logo {
  display: none;
  cursor: pointer;
  text-align: center;
  font-size: 34px;
  line-height: 1;
}

.brand-smpl {
  color: #50e3c2;
  font-weight: 400;
}

.brand-sale {
  color: #0f172a;
  font-weight: 900;
}

.form-title {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
}

.form-subtitle {
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 4px;
}

.login-btn {
  min-width: 120px;
}

@media (max-width: 960px) {
  .mobile-logo {
    display: block;
  }

  .form-title,
  .form-subtitle {
    text-align: center;
  }
}

@media (max-width: 600px) {
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .login-btn {
    width: 100%;
  }
}
</style>